import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Mail, Plus, RefreshCw, Settings2, Search, Users } from "lucide-react";
import Header from "../components/dashboard/Header";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useLocationOptions } from "../components/AvailabilityPanel";
import { EmailDrawer, fmtDay } from "../components/newsletters/EmailDrawer";
import { NewNewsletterDialog } from "../components/newsletters/NewNewsletterDialog";
import { ListSettingsDialog } from "../components/newsletters/ListSettingsDialog";
import { NewsletterCalendar, BestTimeCard } from "../components/newsletters/NewsletterCalendar";

const TABS = [["sent", "Sent"], ["scheduled", "Scheduled"], ["draft", "Drafts"], ["template", "Templates"]];
const inTab = (e, k) => (k === "scheduled" ? e.state === "scheduled" || (e.state === "draft" && e.ready) : e.state === k);
const pct = (n, d) => (d ? `${((n / d) * 100).toFixed(1)}%` : "—");
const fmtDate = (s) => (s ? new Date(s).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—");

function EmailRow({ e, onOpen }) {
  const s = e.stats;
  return (
    <tr onClick={() => onOpen(e.id)} className="border-b border-brand-border last:border-0 hover:bg-brand-hover cursor-pointer transition-colors" data-testid={`nl-row-${e.id}`}>
      <td className="px-4 py-3 max-w-[360px]">
        <p className="text-sm text-brand-ink truncate">{e.name}{e.state === "draft" && e.ready && <span data-testid={`nl-ready-${e.id}`} className="ml-2 text-[10px] uppercase tracking-[0.1em] rounded-full bg-brand-primary/10 text-brand-primary px-2 py-0.5">Ready · sends {fmtDay(e.ready.send_date)}</span>}</p>
        <p className="text-[11px] text-brand-tertiary truncate">{e.subject}</p>
      </td>
      <td className="px-4 py-3 text-xs text-brand-muted whitespace-nowrap">{fmtDate(e.publish_date || e.updated_at)}</td>
      <td className="px-4 py-3 text-right font-mono text-xs text-brand-ink">{s ? s.delivered.toLocaleString() : "—"}</td>
      <td className="px-4 py-3 text-right font-mono text-xs text-brand-ink">{s ? pct(s.open, s.delivered) : "—"}</td>
      <td className="px-4 py-3 text-right font-mono text-xs text-brand-ink">{s ? pct(s.click, s.delivered) : "—"}</td>
      <td className="px-4 py-3 text-right font-mono text-xs text-brand-muted">{s ? s.unsubscribed : "—"}</td>
    </tr>
  );
}

function Summary({ emails }) {
  const sent = emails.filter((e) => e.state === "sent" && e.stats);
  const tot = sent.reduce((a, e) => ({ d: a.d + e.stats.delivered, o: a.o + e.stats.open, c: a.c + e.stats.click }), { d: 0, o: 0, c: 0 });
  const items = [["Newsletters sent", sent.length], ["Delivered", tot.d.toLocaleString()], ["Avg open rate", pct(tot.o, tot.d)], ["Avg click rate", pct(tot.c, tot.d)]];
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="nl-summary">
      {items.map(([l, v]) => (
        <div key={l} className="bg-brand-surface border border-brand-border rounded-xl p-5">
          <p className="text-[11px] uppercase tracking-[0.12em] text-brand-tertiary">{l}</p>
          <p className="font-serif text-3xl text-brand-ink mt-2">{v}</p>
        </div>
      ))}
    </div>
  );
}

export default function Newsletters() {
  const { user } = useAuth();
  const locations = useLocationOptions();
  const [location, setLocation] = useState(() => new URLSearchParams(window.location.search).get("location") || "");
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("sent");
  const [q, setQ] = useState("");
  const [openId, setOpenId] = useState(null);
  const [creating, setCreating] = useState(false);
  const [settings, setSettings] = useState(false);
  const [busy, setBusy] = useState(false);
  const [view, setView] = useState("list");

  useEffect(() => { if (!location && locations.length) setLocation(locations[0]); }, [locations, location]);
  const load = () => {
    if (!location) return;
    setData(null);
    api.get("/newsletters/overview", { params: { location } }).then(({ data: d }) => setData(d))
      .catch((e) => { setData({ emails: [], lists: [], error: formatApiErrorDetail(e.response?.data?.detail) }); });
  };
  useEffect(load, [location]); // eslint-disable-line react-hooks/exhaustive-deps

  const refresh = async () => {
    setBusy(true);
    try { await api.post("/newsletters/refresh"); load(); toast.success("Synced with HubSpot"); }
    catch (e) { toast.error("Sync failed", { description: formatApiErrorDetail(e.response?.data?.detail) }); }
    finally { setBusy(false); }
  };

  const startNew = () => {
    if (data?.lists?.length) return setCreating(true);
    toast.error(`No HubSpot recipient list is set for ${location} yet`, {
      description: user?.role === "admin" ? "Pick the list this location sends to, then create the newsletter." : "Ask an admin to map one under Lists.",
    });
    if (user?.role === "admin") setSettings(true);
  };
  const emails = data?.emails || [];
  const rows = useMemo(() => emails.filter((e) => inTab(e, tab) && (!q || `${e.name} ${e.subject}`.toLowerCase().includes(q.toLowerCase()))), [emails, tab, q]);
  const counts = Object.fromEntries(TABS.map(([k]) => [k, emails.filter((e) => inTab(e, k)).length]));

  return (
    <div className="min-h-screen bg-brand-page relative z-10">
      <Header />
      <main className="max-w-[1280px] mx-auto px-6 lg:px-12 py-10 space-y-8">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-brand-tertiary">Sales &amp; Marketing</p>
            <h1 className="font-serif text-4xl lg:text-5xl font-light tracking-tight text-brand-ink mt-2">Newsletters</h1>
            <p className="text-sm text-brand-muted mt-1">HubSpot member emails for each location.</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <select value={location} onChange={(e) => setLocation(e.target.value)} data-testid="nl-location" className="border border-brand-border rounded-md px-3 py-2 text-sm bg-brand-surface text-brand-ink">
              {locations.map((l) => <option key={l} value={l}>{l}</option>)}
            </select>
            <button onClick={refresh} disabled={busy} data-testid="nl-refresh" className="inline-flex items-center gap-1.5 px-3 py-2 text-sm text-brand-muted hover:text-brand-ink"><RefreshCw className={`h-4 w-4 ${busy ? "animate-spin" : ""}`} /> Sync</button>
            {user?.role === "admin" && <button onClick={() => setSettings(true)} data-testid="nl-settings" className="inline-flex items-center gap-1.5 px-3 py-2 text-sm text-brand-muted hover:text-brand-ink"><Settings2 className="h-4 w-4" /> Lists</button>}
            <button onClick={startNew} disabled={!data} data-testid="nl-new" className="inline-flex items-center gap-1.5 rounded-md bg-brand-ink text-brand-page px-4 py-2 text-sm hover:bg-brand-primary disabled:opacity-50"><Plus className="h-4 w-4" /> New newsletter</button>
          </div>
        </div>

        {data && (
          <div className="flex flex-wrap items-center gap-2 text-sm" data-testid="nl-recipients">
            <Users className="h-4 w-4 text-brand-tertiary" />
            {data.lists?.length ? <>Sends from the hub go to <span className="text-brand-ink font-medium">{data.lists.map((l) => `${l.name}${l.size != null ? ` (${l.size.toLocaleString()})` : ""}`).join(", ")}</span></>
              : <span className="text-brand-gold">No recipient list mapped for {location} yet{user?.role === "admin" ? ". Click Lists to set one." : ". Ask an admin to set one."}</span>}
          </div>
        )}
        {data?.error && <p className="text-sm text-brand-neg" data-testid="nl-error">{data.error}</p>}

        <div className="inline-flex rounded-md border border-brand-border overflow-hidden" data-testid="nl-view">
          {[["list", `${location} list`], ["calendar", "All-locations calendar"]].map(([k, l]) => (
            <button key={k} onClick={() => setView(k)} data-testid={`nl-view-${k}`} className={`px-4 py-2 text-sm ${view === k ? "bg-brand-ink text-brand-page" : "bg-brand-surface text-brand-muted hover:text-brand-ink"}`}>{l}</button>
          ))}
        </div>
        {view === "calendar" ? <NewsletterCalendar onOpen={setOpenId} /> : !data ? <div className="h-40 rounded-xl bg-brand-surface border border-brand-border animate-pulse" /> : (
          <>
            <Summary emails={emails} />
            <BestTimeCard location={location} />
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-brand-border">
              <div className="flex gap-1 overflow-x-auto">
                {TABS.map(([k, l]) => (
                  <button key={k} onClick={() => setTab(k)} data-testid={`nl-tab-${k}`} className={`px-4 py-3 text-sm whitespace-nowrap border-b-2 -mb-px ${tab === k ? "border-brand-primary text-brand-ink font-medium" : "border-transparent text-brand-muted hover:text-brand-ink"}`}>
                    {l} <span className="text-brand-tertiary text-xs">{counts[k]}</span>
                  </button>
                ))}
              </div>
              <label className="relative mb-2"><Search className="h-4 w-4 absolute left-2.5 top-2.5 text-brand-tertiary" />
                <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search emails" data-testid="nl-search" className="pl-8 pr-3 py-2 text-sm bg-brand-surface border border-brand-border rounded-md w-56" />
              </label>
            </div>
            <div className="bg-brand-surface border border-brand-border rounded-xl overflow-x-auto">
              <table className="w-full min-w-[720px]">
                <thead><tr className="text-[10px] uppercase tracking-[0.12em] text-brand-tertiary border-b border-brand-border">
                  <th className="text-left font-normal px-4 py-3">Email</th><th className="text-left font-normal px-4 py-3">Date</th>
                  <th className="text-right font-normal px-4 py-3">Delivered</th><th className="text-right font-normal px-4 py-3">Open rate</th>
                  <th className="text-right font-normal px-4 py-3">Click rate</th><th className="text-right font-normal px-4 py-3">Unsubs</th>
                </tr></thead>
                <tbody>
                  {rows.map((e) => <EmailRow key={e.id} e={e} onOpen={setOpenId} />)}
                  {!rows.length && <tr><td colSpan={6} className="px-4 py-10 text-center text-sm text-brand-tertiary" data-testid="nl-empty"><Mail className="h-5 w-5 mx-auto mb-2" />Nothing here yet.</td></tr>}
                </tbody>
              </table>
            </div>
          </>
        )}
      </main>
      <EmailDrawer id={openId} onClose={() => setOpenId(null)} onChanged={load} />
      <NewNewsletterDialog open={creating} onOpenChange={setCreating} location={location} emails={emails} onCreated={(id) => { load(); setOpenId(id); }} />
      {settings && <ListSettingsDialog onClose={() => { setSettings(false); load(); }} />}
    </div>
  );
}
